import { Sparkles, Truck, Undo2, ClipboardCheck, Clock, XCircle, Search } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { SectionPageEditor } from '@/components/admin/SectionPageEditor';
import type { SectionDef } from '@/components/admin/SectionPageEditor';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

// pages/shipping-returns/page.tsx doesn't follow Privacy/Terms's clean
// numbered pattern — it's several distinct named groups (verified
// directly against the page's actual t() calls). Icon names are
// hardcoded, not i18n — not editable here. Dhaka/Outside Dhaka's price
// isn't editable here on purpose: it now reads live from
// core_shipping_rates (Delivery Merchants settings in ERP), the same
// table checkout/order-placement actually charges from — a separate
// site-content copy of the price was exactly how this page and the
// real price drifted apart before. Delivery-time IS editable below
// (not tracked in core_shipping_rates). The condition1-5 ok/not-eligible
// split (3 ok, 2 not) is also fixed in code. shipping.returns.title
// and shipping.track.desc exist in the locale bundle but aren't read
// by the page — excluded as orphaned.
const SHIPPING_SECTIONS: SectionDef[] = [
  {
    key: 'hero',
    title: 'Hero',
    icon: Sparkles,
    texts: [
      { key: 'shipping.hero.label', label: 'Eyebrow Label' },
      { key: 'shipping.hero.heading', label: 'Heading' },
      { key: 'shipping.hero.subtitle', label: 'Subtitle' },
    ],
  },
  {
    key: 'shippingOptions',
    title: 'Shipping Options',
    icon: Truck,
    note: 'Prices shown on the live page come from ERP → Core → Settings → Delivery Merchants, not from here — update the price there.',
    texts: [
      { key: 'shipping.options.heading', label: 'Section Heading' },
      { key: 'shipping.dhaka.title', label: 'Inside Dhaka — Title' },
      { key: 'shipping.dhaka.time', label: 'Inside Dhaka — Delivery Time' },
      { key: 'shipping.dhaka.desc', label: 'Inside Dhaka — Description' },
      { key: 'shipping.outside.title', label: 'Outside Dhaka — Title' },
      { key: 'shipping.outside.time', label: 'Outside Dhaka — Delivery Time' },
      { key: 'shipping.outside.desc', label: 'Outside Dhaka — Description' },
      { key: 'shipping.free.title', label: 'Free Shipping — Title' },
      { key: 'shipping.free.desc', label: 'Free Shipping — Description' },
      { key: 'shipping.track.title', label: 'Free Shipping — Time Label' },
    ],
  },
  {
    key: 'returns',
    title: 'Returns',
    icon: Undo2,
    texts: [
      { key: 'shipping.returns.heading', label: 'Heading' },
      { key: 'shipping.returns.desc', label: 'Description', long: true },
      { key: 'shipping.step1.title', label: 'Step 1 — Title' },
      { key: 'shipping.step1.desc', label: 'Step 1 — Description' },
      { key: 'shipping.step2.title', label: 'Step 2 — Title' },
      { key: 'shipping.step2.desc', label: 'Step 2 — Description' },
      { key: 'shipping.step3.title', label: 'Step 3 — Title' },
      { key: 'shipping.step3.desc', label: 'Step 3 — Description' },
    ],
  },
  {
    key: 'conditions',
    title: 'Return Conditions',
    icon: ClipboardCheck,
    note: 'Which conditions count as "eligible" vs "not eligible" (3 vs 2) is fixed in code — only the text below is editable here.',
    texts: [
      { key: 'shipping.conditions.heading', label: 'Heading' },
      { key: 'shipping.condition1', label: 'Condition 1' },
      { key: 'shipping.condition2', label: 'Condition 2' },
      { key: 'shipping.condition3', label: 'Condition 3' },
      { key: 'shipping.condition4', label: 'Condition 4' },
      { key: 'shipping.condition5', label: 'Condition 5' },
    ],
  },
  {
    key: 'refundTimeline',
    title: 'Refund Timeline',
    icon: Clock,
    texts: [
      { key: 'shipping.refundTimeline.heading', label: 'Heading' },
      { key: 'shipping.refundTimeline.desc', label: 'Description', long: true },
    ],
  },
  {
    key: 'cancellation',
    title: 'Cancellation',
    icon: XCircle,
    texts: [
      { key: 'shipping.cancellation.heading', label: 'Heading' },
      { key: 'shipping.cancellation.desc', label: 'Description', long: true },
    ],
  },
  {
    key: 'seo',
    title: 'SEO',
    icon: Search,
    note: 'Shown in Google search results and social media link previews — not visible on the page itself.',
    texts: [
      { key: 'seo.shipping.title', label: 'Meta Title', long: true },
      { key: 'seo.shipping.description', label: 'Meta Description', long: true },
    ],
  },
];

export default function AdminShippingPage() {
  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <SectionPageEditor pageKey="shipping" title="Shipping & Returns" sections={SHIPPING_SECTIONS} />
    </AppShell>
  );
}
